import { useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { getLocalizedProductName } from '../utils/multilingualUtils';

export const useCartValidation = () => {
    const [isValidating, setIsValidating] = useState(false);

    const validateCartItems = async (cartItems, storeId) => {
        setIsValidating(true);

        try {
            const validation = {
                isValid: true,
                unavailableItems: [],
                insufficientStockItems: []
            };

            // //console.log("Starting cart validation for items:", cartItems);
            // console.log("Store ID:", storeId);

            for (const item of cartItems) {
                try {
                    // console.log(`Validating item: ${getLocalizedProductName(item, 'en')} (${item.productId})`);

                    const productRef = doc(db, `stores/${storeId}/products`, item.productId);
                    const productSnap = await getDoc(productRef);

                    if (!productSnap.exists()) {
                        // console.log(`Product ${item.productId} not found in Firestore`);
                        validation.isValid = false;
                        validation.unavailableItems.push(item);
                        continue;
                    }

                    const productData = productSnap.data();
                    // //console.log(`Product data for ${item.productId}:`, productData);

                    // Check if product is still active (be more flexible with status check)
                    if (productData.status && productData.status.toLowerCase() !== 'active' && productData.status.toLowerCase() !== 'published') {
                        //console.log(`Product ${item.productId} has inactive status: ${productData.status}`);
                        validation.isValid = false;
                        validation.unavailableItems.push(item);
                        continue;
                    }

                    // Check stock for variant or main product
                    let availableStock = 0;

                    if (item.variantId && productData.variants && productData.variants.length > 0) {
                        //console.log(`Checking variant ${item.variantId} for product ${item.productId}`);
                        const variant = productData.variants.find(v => v.id === item.variantId);
                        if (!variant) {
                            //console.log(`Variant ${item.variantId} not found`);
                            validation.isValid = false;
                            validation.unavailableItems.push(item);
                            continue;
                        }
                        availableStock = variant.quantity || 0;
                        //console.log(`Variant stock: ${availableStock}`);
                    } else {
                        // For products without variants, check main product stock
                        // FIXED: Check both 'quantity' and 'stock' fields for backwards compatibility
                        availableStock = productData.quantity || productData.stock || 0;
                        //console.log(`Product stock (checking quantity field): ${availableStock}`);
                        //console.log(`Product data has variants array:`, productData.variants ? productData.variants.length : 'none');
                    }

                    // Skip quantity validation for infinite/unlimited stock (-1)
                    if (parseInt(availableStock) === -1) {
                        //console.log(`✓ Item ${item.productId} has unlimited stock, skipping quantity check`);
                        continue;
                    }

                    // Check if requested quantity is available
                    if (availableStock < item.quantity) {
                        //console.log(`Insufficient stock for ${item.productId}. Available: ${availableStock}, Requested: ${item.quantity}`);
                        validation.isValid = false;
                        validation.insufficientStockItems.push({
                            ...item,
                            availableQuantity: availableStock,
                            requestedQuantity: item.quantity
                        });
                    } else {
                        //console.log(`✓ Item ${item.productId} validation passed`);
                    }

                } catch (error) {
                    console.error(`Error validating item ${item.productId}:`, error);
                    // If we can't validate an item due to an error, consider it unavailable
                    validation.isValid = false;
                    validation.unavailableItems.push(item);
                }
            }

            //console.log("Validation result:", validation);
            return validation;
        } catch (error) {
            console.error("Cart validation failed:", error);
            // If validation fails completely, assume items are unavailable
            return {
                isValid: false,
                unavailableItems: cartItems,
                insufficientStockItems: []
            };
        } finally {
            setIsValidating(false);
        }
    };

    const removeUnavailableItems = async (cart, unavailableItems, currentUser) => {
        const unavailableIds = unavailableItems.map(item =>
            item.variantId ? `${item.productId}_${item.variantId}` : item.productId
        );

        const updatedItems = cart.items.filter(item => {
            const itemId = item.variantId ? `${item.productId}_${item.variantId}` : item.productId;
            return !unavailableIds.includes(itemId);
        });

        const updatedCart = {
            ...cart,
            items: updatedItems,
            subtotal: updatedItems.reduce((total, item) => total + (item.price * item.quantity), 0)
        };

        // Update cart using existing functions from cart-functions.js
        try {
            if (currentUser) {
                // For logged-in users, update Firestore
                const cartRef = doc(db, "users", currentUser.uid, "cart", "current");
                await setDoc(cartRef, {
                    ...updatedCart,
                    updatedAt: serverTimestamp()
                });
            } else {
                // For guest users, update localStorage
                localStorage.setItem('guestCart', JSON.stringify({
                    ...updatedCart,
                    updatedAt: new Date().toISOString()
                }));
            }
        } catch (error) {
            console.error("Error updating cart after removing unavailable items:", error);
            // Fallback to localStorage for all users if Firestore fails
            localStorage.setItem('guestCart', JSON.stringify({
                ...updatedCart,
                updatedAt: new Date().toISOString()
            }));
        }

        return updatedCart;
    };

    return {
        validateCartItems,
        removeUnavailableItems,
        isValidating
    };
};